import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { mloPhotoUrl } from "@/lib/photo";
import { motion, AnimatePresence } from "framer-motion";
import {
  Lock,
  Loader2,
  Upload,
  Download,
  Trash2,
  FileText,
  Image as ImageIcon,
  Film,
  Music,
  Archive,
  File as FileIcon,
  LogOut,
  ShieldCheck,
  X,
  LayoutDashboard,
  Share2,
  Check,
  Copy,
  Eye,
  EyeOff,
  ExternalLink,
  Plus,
  Pencil,
  Send,
  Printer,
  Star,
} from "lucide-react";
import { ASSETS } from "@/lib/constants";
import { ReplaysGrid } from "@/components/ReplaysGrid";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VIEWER_TOKEN_KEY = "grc_library_token";
const ADMIN_TOKEN_KEY = "grc_admin_token";

const CATEGORIES = [
  { key: "marketing", label: "Marketing Assets", blurb: "Logos, images, videos, one-pagers, branded creative." },
  { key: "messaging", label: "Messaging & Outreach", blurb: "Email copy, SMS scripts, social captions, talking points." },
  { key: "process", label: "Process", blurb: "SOPs, checklists, workflows, playbooks." },
  { key: "other", label: "Other Documents", blurb: "Everything else — decks, agreements, references." },
  { key: "print", label: "Print Cards", blurb: "Print-ready cards & postcards for every MLO — download, print on cardstock, done." },
  { key: "replays", label: "Replays", blurb: "Recorded monthly partner meetings and short training clips — watch anytime." },
];

const PRINT_PIECES = [
  { key: "openhouse", label: "Open house card", desc: "Quarter-page cardstock (4.25×5.5) — set out at open houses and events." },
  { key: "checkup", label: "Annual rate-checkup postcard", desc: "6×4.25 postcard — mail or hand to past clients for a yearly rate checkup." },
  { key: "closinggift", label: "Closing-gift card", desc: "5.5×4.25 insert — tuck into closing gifts to plant the year-two checkup." },
];

function PrintCardsPanel() {
  const [mlos, setMlos] = useState(null);
  useEffect(() => {
    axios
      .get(`${API}/mlos/public`)
      .then((r) => setMlos(r.data || []))
      .catch(() => setMlos([]));
  }, []);

  if (mlos === null) {
    return (
      <div className="py-24 flex items-center justify-center text-[#7A7668]">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading print cards…
      </div>
    );
  }
  return (
    <div className="space-y-6" data-testid="library-print-panel">
      <div className="rounded-2xl bg-[#0F2A47] text-white px-5 py-4 text-sm leading-relaxed">
        Each PDF is generated fresh with that MLO's name, NMLS, contact info, and a QR code straight to
        their page — so it's always current. Print on cardstock (100 lb+ recommended).
      </div>
      {mlos.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-[#D9D3BF] bg-white/60 py-16 text-center">
          <Printer className="w-8 h-8 text-[#7A7668] mx-auto mb-2" />
          <p className="text-[#0F2A47] font-medium">No MLOs available yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {mlos.map((m) => (
            <div key={m.slug} className="rounded-3xl bg-white border border-[#E6E2D6] shadow-sm p-6" data-testid={`library-print-mlo-${m.slug}`}>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full overflow-hidden bg-[#F5F2E8] border border-[#E6E2D6] shrink-0">
                  {m.has_photo ? (
                    <img src={mloPhotoUrl(m)} alt={m.name} className="w-full h-full object-cover object-top" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#7A7668] text-sm font-semibold">
                      {(m.name || "?").slice(0, 1)}
                    </div>
                  )}
                </div>
                <div>
                  <div className="font-semibold text-[#0F2A47]">{m.name}</div>
                  <div className="text-xs text-[#7A7668]">{m.nmls ? `NMLS #${m.nmls}` : m.title || ""}</div>
                </div>
              </div>
              <ul className="mt-4 divide-y divide-[#F0EDE2]">
                {PRINT_PIECES.map((p) => (
                  <li key={p.key} className="py-2.5 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#0F2A47]">{p.label}</div>
                      <div className="text-[11px] text-[#7A7668]">{p.desc}</div>
                    </div>
                    <a
                      href={`${API}/mlos/${m.slug}/print/${p.key}.pdf`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`library-print-${m.slug}-${p.key}`}
                      className="shrink-0 inline-flex items-center gap-1.5 text-xs font-medium bg-[#0F2A47] text-white px-3 py-1.5 rounded-full hover:bg-[#173a5f] transition"
                    >
                      <Printer className="w-3 h-3" /> View & print
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function fmtBytes(bytes) {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
}

function iconFor(ctype) {
  if (!ctype) return FileIcon;
  if (ctype.startsWith("image/")) return ImageIcon;
  if (ctype.startsWith("video/")) return Film;
  if (ctype.startsWith("audio/")) return Music;
  if (ctype.includes("zip")) return Archive;
  if (ctype === "application/pdf" || ctype.includes("word") || ctype.includes("document") || ctype.startsWith("text/") || ctype.includes("presentation") || ctype.includes("sheet")) return FileText;
  return FileIcon;
}

// ---------- Password Gate ----------
function PasswordGate({ onUnlock }) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const res = await axios.post(`${API}/library/verify`, { password });
      localStorage.setItem(VIEWER_TOKEN_KEY, res.data.access_token);
      onUnlock();
    } catch (err) {
      setError("Incorrect password. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-16"
      style={{
        background:
          "radial-gradient(1200px 600px at 20% -10%, #17385e 0%, #0F2A47 40%, #08192F 100%)",
      }}
      data-testid="library-gate"
    >
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="w-full max-w-md rounded-3xl bg-white/95 backdrop-blur border border-white/40 shadow-2xl p-8"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 rounded-2xl bg-[#0F2A47]/10">
            <ShieldCheck className="w-6 h-6 text-[#0F2A47]" />
          </div>
          <img src={ASSETS.logoHorizontal} alt="Great Rate Check" className="h-10 w-auto" />
        </div>
        <h1 className="text-2xl font-semibold text-[#0F2A47] tracking-tight">Partner Asset Library</h1>
        <p className="text-sm text-[#59564C] mt-2 leading-relaxed">
          This page is protected. Please enter the shared access password to view marketing, messaging, and process documents.
        </p>
        <label className="block mt-6 text-xs uppercase tracking-widest text-[#59564C]">Password</label>
        <div className="mt-1 relative">
          <Lock className="w-4 h-4 text-[#7A7668] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="library-password-input"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full pl-9 pr-11 py-3 rounded-xl border border-[#E6E2D6] focus:border-[#1F8F8B] focus:outline-none focus:ring-2 focus:ring-[#1F8F8B]/20 bg-white text-[#0F2A47]"
            placeholder="Enter password"
            autoFocus
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? "Hide password" : "Show password"}
            data-testid="library-password-toggle"
            className="absolute inset-y-0 right-2 flex items-center px-2 text-[#7A7668] hover:text-[#0F2A47]"
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        {error && (
          <p data-testid="library-password-error" className="mt-2 text-sm text-red-600">
            {error}
          </p>
        )}
        <button
          type="submit"
          data-testid="library-password-submit"
          disabled={busy || !password}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#0F2A47] hover:bg-[#173a5f] text-white py-3 font-medium disabled:opacity-60 transition"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Unlock library
        </button>
        <p className="mt-4 text-[11px] text-[#7A7668] text-center">
          Great Rate Check · NEXA Lending · Partner materials only
        </p>
      </motion.form>
    </div>
  );
}

// ---------- Upload Panel ----------
function UploadPanel({ category, subcategories, folders, defaultSubcategoryId, defaultFolderId, onUploaded, onClose }) {
  const [files, setFiles] = useState([]);
  const [description, setDescription] = useState("");
  const [subcategoryId, setSubcategoryId] = useState(defaultSubcategoryId || (subcategories[0]?.id ?? ""));
  const [folderId, setFolderId] = useState(defaultFolderId || "");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  // Only show folders that belong to the currently selected subcategory
  const folderOptions = useMemo(
    () => (folders || []).filter((f) => f.subcategory_id === subcategoryId),
    [folders, subcategoryId],
  );

  // Reset folder if user switches subcategory (folders are subcategory-scoped)
  useEffect(() => {
    setFolderId((prev) => (folderOptions.find((f) => f.id === prev) ? prev : ""));
  }, [subcategoryId, folderOptions]);

  const onPick = (e) => {
    const list = Array.from(e.target.files || []);
    setFiles(list);
  };

  const upload = async () => {
    if (!files.length) return;
    if (!subcategoryId) {
      setErr("Please pick a subcategory.");
      return;
    }
    setBusy(true);
    setErr("");
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("category", category);
      fd.append("subcategory_id", subcategoryId);
      if (folderId) fd.append("folder_id", folderId);
      fd.append("description", description);
      files.forEach((f) => fd.append("files", f));
      const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
      await axios.post(`${API}/library/admin/assets`, fd, {
        headers: {
          Authorization: `Bearer ${adminToken}`,
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      onUploaded();
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Upload failed. Are you still logged in as admin?");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4"
      data-testid="library-upload-modal"
    >
      <motion.div
        initial={{ scale: 0.96, y: 12 }}
        animate={{ scale: 1, y: 0 }}
        className="bg-white rounded-3xl w-full max-w-xl shadow-2xl overflow-hidden"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#EDE9DC]">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#1F8F8B]">Upload</div>
            <div className="text-lg font-semibold text-[#0F2A47]">
              {CATEGORIES.find((c) => c.key === category)?.label || "Assets"}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-[#F4F2EB]">
            <X className="w-4 h-4 text-[#59564C]" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div
            onClick={() => inputRef.current?.click()}
            className="border-2 border-dashed border-[#D9D3BF] rounded-2xl px-4 py-8 text-center cursor-pointer hover:border-[#1F8F8B] transition"
            data-testid="library-upload-dropzone"
          >
            <Upload className="w-6 h-6 text-[#1F8F8B] mx-auto" />
            <p className="mt-2 text-sm text-[#0F2A47] font-medium">Click to choose files</p>
            <p className="text-xs text-[#7A7668]">Images, PDFs, Word, PPT, Video, ZIP — up to 25MB each</p>
            <input
              ref={inputRef}
              type="file"
              multiple
              onChange={onPick}
              className="hidden"
              data-testid="library-upload-input"
            />
          </div>
          {files.length > 0 && (
            <ul className="text-sm text-[#0F2A47] space-y-1 max-h-40 overflow-auto">
              {files.map((f, i) => (
                <li key={i} className="flex justify-between border-b border-[#F4F2EB] py-1">
                  <span className="truncate mr-3">{f.name}</span>
                  <span className="text-[#7A7668] text-xs">{fmtBytes(f.size)}</span>
                </li>
              ))}
            </ul>
          )}
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-[#59564C]">Subcategory <span className="text-red-500">*</span></span>
            <select
              value={subcategoryId}
              onChange={(e) => setSubcategoryId(e.target.value)}
              data-testid="library-upload-subcategory"
              className="mt-1 w-full rounded-xl border border-[#E6E2D6] focus:border-[#1F8F8B] focus:outline-none focus:ring-2 focus:ring-[#1F8F8B]/20 p-3 text-sm text-[#0F2A47] bg-white"
            >
              {subcategories.length === 0 && <option value="">— none available —</option>}
              {subcategories.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-[#59564C]">Folder <span className="text-[#7A7668] normal-case tracking-normal">(optional)</span></span>
            <select
              value={folderId}
              onChange={(e) => setFolderId(e.target.value)}
              data-testid="library-upload-folder"
              className="mt-1 w-full rounded-xl border border-[#E6E2D6] focus:border-[#1F8F8B] focus:outline-none focus:ring-2 focus:ring-[#1F8F8B]/20 p-3 text-sm text-[#0F2A47] bg-white"
            >
              <option value="">— Ungrouped —</option>
              {folderOptions.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
            {folderOptions.length === 0 && (
              <p className="mt-1 text-[11px] text-[#7A7668]">Create a folder from the library page to group these files together.</p>
            )}
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-[#59564C]">Description (optional)</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Short note about these files (visible to partners)"
              className="mt-1 w-full rounded-xl border border-[#E6E2D6] focus:border-[#1F8F8B] focus:outline-none focus:ring-2 focus:ring-[#1F8F8B]/20 p-3 text-sm text-[#0F2A47]"
              data-testid="library-upload-description"
            />
          </label>
          {busy && (
            <div className="w-full bg-[#F4F2EB] rounded-full h-2 overflow-hidden">
              <div
                className="h-full bg-[#1F8F8B] transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
          {err && <p className="text-sm text-red-600" data-testid="library-upload-error">{err}</p>}
        </div>
        <div className="px-6 py-4 bg-[#FDFBF7] flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-[#0F2A47] hover:bg-[#EDE9DC]"
          >
            Cancel
          </button>
          <button
            onClick={upload}
            disabled={busy || !files.length}
            data-testid="library-upload-submit"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-[#0F2A47] hover:bg-[#173a5f] text-white disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Upload {files.length ? `(${files.length})` : ""}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ---------- Share Link Modal ----------
function ShareModal({ asset, onClose }) {
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState("");

  const generate = async () => {
    setBusy(true);
    setErr("");
    setCopied(false);
    try {
      const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
      const res = await axios.post(
        `${API}/library/admin/assets/${asset.id}/share-link`,
        { expires_days: Number(days) || 30 },
        { headers: { Authorization: `Bearer ${adminToken}` } },
      );
      // Build URL against the current site (works for preview + production)
      const site = window.location.origin;
      const full = `${site}/library/share/${res.data.share_token}`;
      setUrl(full);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not generate link.");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  useEffect(() => {
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset.id]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4"
      data-testid="library-share-modal"
    >
      <motion.div
        initial={{ scale: 0.96, y: 12 }}
        animate={{ scale: 1, y: 0 }}
        className="bg-white rounded-3xl w-full max-w-xl shadow-2xl overflow-hidden"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#EDE9DC]">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#1F8F8B]">Share</div>
            <div className="text-lg font-semibold text-[#0F2A47] truncate max-w-md">{asset.filename}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-[#F4F2EB]">
            <X className="w-4 h-4 text-[#59564C]" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-[#59564C]">
            Anyone with this link can view and download this single file — no library password required.
            The link expires automatically.
          </p>

          <label className="block">
            <span className="text-xs uppercase tracking-widest text-[#59564C]">Expires in</span>
            <div className="flex items-center gap-2 mt-1">
              <select
                value={days}
                onChange={(e) => setDays(e.target.value)}
                className="rounded-xl border border-[#E6E2D6] focus:border-[#1F8F8B] focus:outline-none focus:ring-2 focus:ring-[#1F8F8B]/20 p-2.5 text-sm text-[#0F2A47]"
                data-testid="library-share-expiry"
              >
                <option value={1}>1 day</option>
                <option value={7}>7 days</option>
                <option value={30}>30 days</option>
                <option value={90}>90 days</option>
                <option value={365}>1 year</option>
              </select>
              <button
                onClick={generate}
                disabled={busy}
                className="text-sm px-3 py-2 rounded-lg bg-[#0F2A47] hover:bg-[#173a5f] text-white disabled:opacity-60"
                data-testid="library-share-regenerate"
              >
                {busy ? "Generating…" : "Regenerate"}
              </button>
            </div>
          </label>

          <label className="block">
            <span className="text-xs uppercase tracking-widest text-[#59564C]">Share URL</span>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="text"
                readOnly
                value={url}
                onClick={(e) => e.target.select()}
                data-testid="library-share-url"
                className="flex-1 rounded-xl border border-[#E6E2D6] p-2.5 text-sm text-[#0F2A47] font-mono bg-[#FDFBF7]"
                placeholder={busy ? "Generating…" : ""}
              />
              <button
                onClick={copy}
                disabled={!url}
                data-testid="library-share-copy"
                className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-[#1F8F8B] hover:bg-[#177977] text-white text-sm disabled:opacity-60"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
          </label>

          {err && <p className="text-sm text-red-600">{err}</p>}
        </div>
        <div className="px-6 py-4 bg-[#FDFBF7] flex items-center justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-[#0F2A47] hover:bg-[#EDE9DC]"
          >
            Done
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ---------- Preview Modal (in-browser viewer) ----------
function PreviewModal({ asset, onClose, onDownload, onShare, isAdmin }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [officeUrl, setOfficeUrl] = useState(null); // for Office online viewer via share link
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const ctype = asset.content_type || "";
  const isImage = ctype.startsWith("image/");
  const isPdf = ctype === "application/pdf" || asset.filename?.toLowerCase().endsWith(".pdf");
  const isVideo = ctype.startsWith("video/");
  const isAudio = ctype.startsWith("audio/");
  const isOffice =
    ctype.includes("wordprocessingml") ||
    ctype.includes("presentationml") ||
    ctype.includes("spreadsheetml") ||
    /\.(docx|pptx|xlsx)$/i.test(asset.filename || "");

  useEffect(() => {
    let revoked = false;
    let url = null;
    async function load() {
      setLoading(true);
      setErr("");
      // For preview-able types (image/pdf/video/audio), fetch bytes → blob URL
      if (!(isImage || isPdf || isVideo || isAudio)) {
        setLoading(false);
        return;
      }
      try {
        const token =
          localStorage.getItem(ADMIN_TOKEN_KEY) || localStorage.getItem(VIEWER_TOKEN_KEY);
        const res = await fetch(`${API}/library/assets/${asset.id}/preview`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to load preview");
        const blob = await res.blob();
        if (revoked) return;
        url = URL.createObjectURL(blob);
        setPreviewUrl(url);
      } catch (e) {
        setErr("Preview couldn't be loaded. You can still download the file.");
      } finally {
        setLoading(false);
      }
    }
    load();
    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset.id]);

  // For Office docs — generate a temporary share link (public URL) so Microsoft's viewer can fetch it
  const openInOfficeViewer = async () => {
    try {
      const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
      const viewerToken = localStorage.getItem(VIEWER_TOKEN_KEY);
      let sharePublicUrl;
      if (adminToken) {
        const res = await axios.post(
          `${API}/library/admin/assets/${asset.id}/share-link`,
          { expires_days: 1 },
          { headers: { Authorization: `Bearer ${adminToken}` } },
        );
        sharePublicUrl = `${window.location.origin.replace(/\/$/, "")}${
          new URL(`${API}/library/share/${res.data.share_token}/download`).pathname
        }`;
        // Build via API base to include /api
        sharePublicUrl = `${API}/library/share/${res.data.share_token}/download`;
      } else {
        setErr("Only admins can open in-browser Office viewer right now.");
        return;
      }
      const officeSrc = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
        sharePublicUrl,
      )}`;
      setOfficeUrl(officeSrc);
    } catch (e) {
      setErr("Could not open Office viewer.");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex flex-col"
      data-testid="library-preview-modal"
      onClick={onClose}
    >
      {/* Top bar */}
      <div
        className="flex items-center justify-between px-4 sm:px-6 py-3 bg-[#0F2A47] text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-[#A6D24A]">Preview</div>
          <div className="text-sm sm:text-base font-semibold truncate">{asset.filename}</div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => onDownload(asset)}
            data-testid="library-preview-download"
            className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 hover:bg-white/20 px-3 py-1.5 text-sm"
          >
            <Download className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Download</span>
          </button>
          {isAdmin && (
            <button
              onClick={() => onShare(asset)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 hover:bg-white/20 px-3 py-1.5 text-sm"
            >
              <Share2 className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Share</span>
            </button>
          )}
          <button
            onClick={onClose}
            data-testid="library-preview-close"
            className="p-2 rounded-lg hover:bg-white/10"
            aria-label="Close preview"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div
        className="flex-1 flex items-center justify-center p-2 sm:p-6 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {loading ? (
          <div className="text-white/80 inline-flex items-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading preview…
          </div>
        ) : isImage && previewUrl ? (
          <img
            src={previewUrl}
            alt={asset.filename}
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl bg-white"
          />
        ) : isPdf && previewUrl ? (
          <iframe
            src={previewUrl}
            title={asset.filename}
            className="w-full h-full bg-white rounded-lg shadow-2xl"
            data-testid="library-preview-pdf"
          />
        ) : isVideo && previewUrl ? (
          <video
            src={previewUrl}
            controls
            autoPlay
            className="max-w-full max-h-full rounded-lg shadow-2xl bg-black"
          />
        ) : isAudio && previewUrl ? (
          <div className="w-full max-w-2xl bg-white rounded-2xl p-8 shadow-2xl">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-4 rounded-2xl bg-[#F4F2EB]">
                <Music className="w-10 h-10 text-[#1F8F8B]" />
              </div>
              <div>
                <div className="font-semibold text-[#0F2A47]">{asset.filename}</div>
                <div className="text-xs text-[#7A7668]">{ctype}</div>
              </div>
            </div>
            <audio src={previewUrl} controls className="w-full" />
          </div>
        ) : isOffice ? (
          <div className="w-full h-full flex flex-col">
            {officeUrl ? (
              <iframe
                src={officeUrl}
                title={asset.filename}
                className="w-full flex-1 bg-white rounded-lg shadow-2xl"
                data-testid="library-preview-office"
              />
            ) : (
              <div className="w-full max-w-lg mx-auto my-auto bg-white rounded-2xl p-8 shadow-2xl text-center">
                <FileText className="w-12 h-12 text-[#1F8F8B] mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-[#0F2A47]">{asset.filename}</h3>
                <p className="text-sm text-[#59564C] mt-2">
                  Office documents can be viewed in your browser via Microsoft's Office Online viewer.
                </p>
                {isAdmin ? (
                  <button
                    onClick={openInOfficeViewer}
                    data-testid="library-preview-office-open"
                    className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#0F2A47] hover:bg-[#173a5f] text-white px-5 py-2.5 text-sm"
                  >
                    <ExternalLink className="w-4 h-4" /> View in browser
                  </button>
                ) : (
                  <p className="mt-4 text-xs text-[#7A7668]">
                    Ask an admin to share a public link for in-browser viewing, or download the file to open locally.
                  </p>
                )}
                <button
                  onClick={() => onDownload(asset)}
                  className="mt-3 inline-flex items-center gap-2 rounded-xl border border-[#EDE9DC] hover:border-[#1F8F8B] text-[#0F2A47] px-5 py-2.5 text-sm"
                >
                  <Download className="w-4 h-4" /> Download instead
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="w-full max-w-lg bg-white rounded-2xl p-8 shadow-2xl text-center">
            <FileIcon className="w-12 h-12 text-[#7A7668] mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-[#0F2A47]">{asset.filename}</h3>
            <p className="text-sm text-[#59564C] mt-2">
              This file type can't be previewed in the browser.
            </p>
            <button
              onClick={() => onDownload(asset)}
              className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#0F2A47] hover:bg-[#173a5f] text-white px-5 py-2.5 text-sm"
            >
              <Download className="w-4 h-4" /> Download to view
            </button>
          </div>
        )}
        {err && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white text-[#B04A2E] rounded-lg px-4 py-2 shadow text-sm">
            {err}
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ---------- Asset Card ----------
function AssetCard({ asset, subcategories = [], folders = [], onDownload, onDelete, onShare, onPreview, onMove, onMoveFolder, onFeature, isAdmin }) {
  const Icon = iconFor(asset.content_type);
  const isImage = (asset.content_type || "").startsWith("image/");
  const isPdf = asset.content_type === "application/pdf";
  const hasThumb = !!asset.thumb_file_id;
  const previewUrl = `${API}/library/assets/${asset.id}/preview`;
  const thumbUrl = `${API}/library/assets/${asset.id}/thumb`;
  const viewerToken = localStorage.getItem(VIEWER_TOKEN_KEY);
  const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    let revoked = false;
    let url = null;
    async function loadThumb() {
      const wantsPreview = isImage || (isPdf && hasThumb);
      if (!wantsPreview) return;
      const src = isImage ? previewUrl : thumbUrl;
      try {
        const token = adminToken || viewerToken;
        const res = await fetch(src, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const blob = await res.blob();
        if (revoked) return;
        url = URL.createObjectURL(blob);
        setBlobUrl(url);
      } catch {}
    }
    loadThumb();
    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset.id, asset.thumb_file_id]);

  const ext = (asset.filename?.split(".").pop() || (asset.content_type || "").split("/").pop() || "file").toUpperCase().slice(0, 5);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="group rounded-2xl overflow-hidden bg-white border border-[#EDE9DC] hover:shadow-xl transition"
      data-testid={`library-asset-${asset.id}`}
    >
      <button
        onClick={() => onPreview(asset)}
        data-testid={`library-open-${asset.id}`}
        className="w-full aspect-[16/10] bg-[#F4F2EB] flex items-center justify-center relative overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#1F8F8B]/40 group/thumb"
        aria-label={`Open ${asset.filename} preview`}
      >
        {blobUrl ? (
          <img
            src={blobUrl}
            alt={asset.filename}
            className={isPdf ? "w-full h-full object-contain bg-white" : "w-full h-full object-cover"}
          />
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Icon className="w-12 h-12 text-[#7A7668]" strokeWidth={1.4} />
            <span className="text-[10px] uppercase tracking-widest text-[#7A7668]">{ext}</span>
          </div>
        )}
        <span className="absolute top-2 right-2 text-[10px] uppercase tracking-widest bg-white/90 px-2 py-1 rounded-md text-[#0F2A47]">
          {ext}
        </span>
        <div className="absolute inset-0 bg-black/0 group-hover/thumb:bg-black/25 transition-colors flex items-center justify-center">
          <span className="opacity-0 group-hover/thumb:opacity-100 transition-opacity text-white text-sm font-medium inline-flex items-center gap-1.5 bg-black/50 backdrop-blur px-3 py-1.5 rounded-full">
            <Eye className="w-4 h-4" /> Preview
          </span>
        </div>
      </button>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-semibold text-[#0F2A47] truncate" title={asset.filename}>
            {asset.filename}
          </p>
        </div>
        {asset.description ? (
          <p className="text-xs text-[#59564C] mt-1 line-clamp-2">{asset.description}</p>
        ) : null}
        <div className="flex items-center justify-between mt-3 text-[11px] text-[#7A7668]">
          <span>{fmtBytes(asset.size)}</span>
          <span>{fmtDate(asset.created_at)}</span>
        </div>
        {(() => {
          const currentSub = subcategories.find((s) => s.id === asset.subcategory_id);
          if (isAdmin && subcategories.length > 0) {
            const folderOpts = (folders || []).filter((f) => f.subcategory_id === asset.subcategory_id);
            return (
              <div className="mt-2 space-y-1.5">
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="text-[#7A7668] shrink-0 w-16">Sub:</span>
                  <select
                    value={asset.subcategory_id || ""}
                    onChange={(e) => onMove && onMove(asset, e.target.value)}
                    data-testid={`library-move-${asset.id}`}
                    className="flex-1 rounded-md border border-[#E6E2D6] bg-white px-2 py-1 text-[11px] text-[#0F2A47] focus:border-[#1F8F8B] focus:outline-none"
                  >
                    {subcategories.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="text-[#7A7668] shrink-0 w-16">Folder:</span>
                  <select
                    value={asset.folder_id || ""}
                    onChange={(e) => onMoveFolder && onMoveFolder(asset, e.target.value)}
                    data-testid={`library-move-folder-${asset.id}`}
                    className="flex-1 rounded-md border border-[#E6E2D6] bg-white px-2 py-1 text-[11px] text-[#0F2A47] focus:border-[#1F8F8B] focus:outline-none"
                    disabled={folderOpts.length === 0}
                  >
                    <option value="">— Ungrouped —</option>
                    {folderOpts.map((f) => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            );
          }
          if (currentSub) {
            return (
              <div className="mt-2">
                <span className="inline-block text-[10px] uppercase tracking-widest bg-[#F4F2EB] text-[#59564C] px-2 py-0.5 rounded-md">
                  {currentSub.name}
                </span>
              </div>
            );
          }
          return null;
        })()}
        <div className="mt-3 flex items-center gap-2">
          <button
            onClick={() => onPreview(asset)}
            data-testid={`library-preview-${asset.id}`}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-[#EDE9DC] hover:border-[#1F8F8B] hover:text-[#1F8F8B] text-[#0F2A47] px-3 py-2 text-sm"
          >
            <Eye className="w-3.5 h-3.5" /> Open
          </button>
          <button
            onClick={() => onDownload(asset)}
            data-testid={`library-download-${asset.id}`}
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#0F2A47] hover:bg-[#173a5f] text-white px-3 py-2 text-sm"
          >
            <Download className="w-3.5 h-3.5" /> Download
          </button>
          {isAdmin && (
            <>
              <button
                onClick={() => onFeature(asset)}
                data-testid={`library-feature-${asset.id}`}
                className={`rounded-lg border p-2 transition-colors ${
                  asset.featured
                    ? "border-[#A6D24A] bg-[#A6D24A]/15 text-[#5a7a1a]"
                    : "border-[#EDE9DC] hover:border-[#A6D24A] text-[#59564C]"
                }`}
                title={asset.featured ? "Featured on program page — click to remove" : "Feature on program page"}
              >
                <Star className={`w-4 h-4 ${asset.featured ? "fill-current" : ""}`} />
              </button>
              <button
                onClick={() => onShare(asset)}
                data-testid={`library-share-${asset.id}`}
                className="rounded-lg border border-[#EDE9DC] hover:border-[#1F8F8B] hover:text-[#1F8F8B] text-[#59564C] p-2"
                title="Copy shareable link"
              >
                <Share2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => onDelete(asset)}
                data-testid={`library-delete-${asset.id}`}
                className="rounded-lg border border-[#EDE9DC] hover:border-red-400 hover:text-red-600 text-[#59564C] p-2"
                title="Delete asset (admin)"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// ---------- Issue Bundle Modal (Copy link + Send by email) ----------
function IssueBundleModal({ target, onClose }) {
  // target = { kind: 'folder' | 'subcategory', id, name }
  const [tab, setTab] = useState("link"); // 'link' | 'email'
  const [days, setDays] = useState(30);
  const [linkBusy, setLinkBusy] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [linkErr, setLinkErr] = useState("");

  const [recipients, setRecipients] = useState("");
  const [subject, setSubject] = useState(`Great Rate Check — ${target.name}`);
  const [note, setNote] = useState("");
  const [sendBusy, setSendBusy] = useState(false);
  const [sentTo, setSentTo] = useState([]);
  const [sendErr, setSendErr] = useState("");
  const [excludedCount, setExcludedCount] = useState(0);

  useEffect(() => {
    if (target.kind !== "subcategory") return;
    const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
    axios
      .get(`${API}/library/admin/subcategories/${target.id}/uncategorized-count`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      })
      .then((r) => setExcludedCount(r.data?.uncategorized_count || 0))
      .catch(() => {});
  }, [target]);

  const kindLabel = target.kind === "folder" ? "Folder" : "Subcategory";
  const shareBase = `library/admin/${target.kind === "folder" ? "folders" : "subcategories"}/${target.id}`;

  const generate = async () => {
    setLinkBusy(true);
    setLinkErr("");
    setCopied(false);
    try {
      const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
      const res = await axios.post(
        `${API}/${shareBase}/share-link`,
        { expires_days: Number(days) || 30 },
        { headers: { Authorization: `Bearer ${adminToken}` } },
      );
      setShareUrl(res.data.share_url);
    } catch (e) {
      setLinkErr(e?.response?.data?.detail || "Could not generate link.");
    } finally {
      setLinkBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  const parseRecipients = () =>
    recipients
      .split(/[\s,;\n]+/)
      .map((s) => s.trim())
      .filter(Boolean);

  const send = async () => {
    const list = parseRecipients();
    if (list.length === 0) {
      setSendErr("Add at least one recipient email.");
      return;
    }
    setSendBusy(true);
    setSendErr("");
    setSentTo([]);
    try {
      const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
      const res = await axios.post(
        `${API}/${shareBase}/issue`,
        { recipients: list, note, subject: subject.trim(), expires_days: Number(days) || 30 },
        { headers: { Authorization: `Bearer ${adminToken}` } },
      );
      setSentTo(res.data.sent_to || list);
      setShareUrl(res.data.share_url || "");
    } catch (e) {
      setSendErr(e?.response?.data?.detail || "Could not send.");
    } finally {
      setSendBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4"
      data-testid="library-issue-modal"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.96, y: 12 }}
        animate={{ scale: 1, y: 0 }}
        className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-[#EDE9DC] flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#A6D24A]">Issue {kindLabel}</div>
            <div className="text-lg font-semibold text-[#0F2A47] mt-0.5">{target.name}</div>
            <p className="text-xs text-[#7A7668] mt-1">Send this {kindLabel.toLowerCase()} to a partner. They see a lightweight page with previews & downloads — no password required.</p>
            {excludedCount > 0 && (
              <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-[11px] text-amber-800" data-testid="library-issue-excluded-note">
                {excludedCount} uncategorized file{excludedCount !== 1 ? "s" : ""} won't be included — only files inside folders go out.
              </div>
            )}
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-[#F4F2EB]" data-testid="library-issue-close">
            <X className="w-4 h-4 text-[#59564C]" />
          </button>
        </div>

        <div className="px-6 pt-4 flex items-center gap-2 border-b border-[#EDE9DC]">
          <button
            onClick={() => setTab("link")}
            data-testid="library-issue-tab-link"
            className={`px-3 py-2 text-sm rounded-t-lg border-b-2 ${tab === "link" ? "border-[#1F8F8B] text-[#0F2A47] font-semibold" : "border-transparent text-[#7A7668]"}`}
          >
            <Copy className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> Copy link
          </button>
          <button
            onClick={() => setTab("email")}
            data-testid="library-issue-tab-email"
            className={`px-3 py-2 text-sm rounded-t-lg border-b-2 ${tab === "email" ? "border-[#1F8F8B] text-[#0F2A47] font-semibold" : "border-transparent text-[#7A7668]"}`}
          >
            <Send className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> Send by email
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label>
              <span className="text-[10px] uppercase tracking-widest text-[#59564C]">Expires in (days)</span>
              <input
                type="number"
                min={1}
                max={365}
                value={days}
                onChange={(e) => setDays(e.target.value)}
                className="mt-1 w-full rounded-lg border border-[#E6E2D6] px-3 py-2 text-sm focus:border-[#1F8F8B] focus:outline-none"
                data-testid="library-issue-days"
              />
            </label>
          </div>

          {tab === "link" ? (
            <div className="space-y-3">
              {!shareUrl ? (
                <button
                  onClick={generate}
                  disabled={linkBusy}
                  data-testid="library-issue-generate"
                  className="inline-flex items-center gap-2 rounded-xl bg-[#0F2A47] hover:bg-[#173a5f] text-white px-4 py-2.5 text-sm disabled:opacity-60"
                >
                  {linkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                  Generate share link
                </button>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={shareUrl}
                      className="flex-1 rounded-lg border border-[#E6E2D6] bg-[#FDFBF7] px-3 py-2 text-sm text-[#0F2A47] focus:outline-none"
                      data-testid="library-issue-link-input"
                    />
                    <button
                      onClick={copy}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-[#1F8F8B] hover:bg-[#177977] text-white px-3 py-2 text-sm"
                      data-testid="library-issue-copy"
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      {copied ? "Copied" : "Copy"}
                    </button>
                  </div>
                  <p className="text-[11px] text-[#7A7668]">Anyone with this link can view & download — expires in {days} day{days === 1 ? "" : "s"}.</p>
                </div>
              )}
              {linkErr && <p className="text-sm text-red-600">{linkErr}</p>}
            </div>
          ) : (
            <div className="space-y-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-[#59564C]">Recipient emails</span>
                <textarea
                  value={recipients}
                  onChange={(e) => setRecipients(e.target.value)}
                  rows={3}
                  placeholder="One per line, or comma-separated"
                  className="mt-1 w-full rounded-lg border border-[#E6E2D6] px-3 py-2 text-sm focus:border-[#1F8F8B] focus:outline-none"
                  data-testid="library-issue-recipients"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-[#59564C]">Email subject</span>
                <input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={`Great Rate Check — ${target.name}`}
                  className="mt-1 w-full rounded-lg border border-[#E6E2D6] px-3 py-2 text-sm focus:border-[#1F8F8B] focus:outline-none"
                  data-testid="library-issue-subject"
                />
                <p className="mt-1 text-[11px] text-[#7A7668]">This is what lands in their inbox — keep it plain-English so they know exactly what they're getting.</p>
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-[#59564C]">Personal note (optional)</span>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  placeholder="Anything to explain why you're sending this…"
                  className="mt-1 w-full rounded-lg border border-[#E6E2D6] px-3 py-2 text-sm focus:border-[#1F8F8B] focus:outline-none"
                  data-testid="library-issue-note"
                />
              </label>
              <button
                onClick={send}
                disabled={sendBusy || !recipients.trim()}
                data-testid="library-issue-send"
                className="inline-flex items-center gap-2 rounded-xl bg-[#0F2A47] hover:bg-[#173a5f] text-white px-4 py-2.5 text-sm disabled:opacity-60"
              >
                {sendBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send to {parseRecipients().length || "…"}
              </button>
              {sendErr && <p className="text-sm text-red-600">{sendErr}</p>}
              {sentTo.length > 0 && (
                <div className="rounded-lg border border-[#A6D24A]/50 bg-[#A6D24A]/10 p-3 text-sm text-[#0F2A47]" data-testid="library-issue-sent">
                  <div className="font-semibold flex items-center gap-1.5">
                    <Check className="w-4 h-4" /> Sent to {sentTo.length} recipient{sentTo.length === 1 ? "" : "s"}
                  </div>
                  <div className="text-[11px] text-[#59564C] mt-1 break-all">{sentTo.join(", ")}</div>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ---------- Main Library page ----------
export default function Library() {
  const [ready, setReady] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [tab, setTab] = useState("marketing");
  const [subcategories, setSubcategories] = useState([]);
  const [folders, setFolders] = useState([]);
  const [activeSubId, setActiveSubId] = useState("all"); // "all" or subcategory id
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [shareAsset, setShareAsset] = useState(null);
  const [previewAsset, setPreviewAsset] = useState(null);
  const [addSubOpen, setAddSubOpen] = useState(false);
  const [newSubName, setNewSubName] = useState("");
  const [subBusy, setSubBusy] = useState(false);
  const [addFolderOpen, setAddFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [folderBusy, setFolderBusy] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState({}); // { [folderId|"ungrouped"]: true/false }
  const [issueTarget, setIssueTarget] = useState(null); // { kind: 'folder'|'subcategory', id, name }

  const isAdmin = useMemo(() => !!localStorage.getItem(ADMIN_TOKEN_KEY), [ready, unlocked, showUpload]);

  useEffect(() => {
    document.title = "Partner Library · Great Rate Check";
    const meta = document.head.querySelector('meta[name="robots"]');
    const el = meta || document.createElement("meta");
    el.setAttribute("name", "robots");
    el.setAttribute("content", "noindex,nofollow");
    if (!meta) document.head.appendChild(el);

    const hasViewer = !!localStorage.getItem(VIEWER_TOKEN_KEY);
    const hasAdmin = !!localStorage.getItem(ADMIN_TOKEN_KEY);
    setUnlocked(hasViewer || hasAdmin);
    setReady(true);
  }, []);

  const currentToken = () =>
    localStorage.getItem(ADMIN_TOKEN_KEY) || localStorage.getItem(VIEWER_TOKEN_KEY);

  const loadSubcategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/library/subcategories`, {
        params: { category: tab },
        headers: { Authorization: `Bearer ${currentToken()}` },
      });
      setSubcategories(res.data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(VIEWER_TOKEN_KEY);
        setUnlocked(false);
      }
    }
  }, [tab]);

  const loadAssets = useCallback(async () => {
    setLoading(true);
    try {
      const params = { category: tab };
      if (activeSubId && activeSubId !== "all") params.subcategory_id = activeSubId;
      const res = await axios.get(`${API}/library/assets`, {
        params,
        headers: { Authorization: `Bearer ${currentToken()}` },
      });
      setAssets(res.data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(VIEWER_TOKEN_KEY);
        setUnlocked(false);
      }
    } finally {
      setLoading(false);
    }
  }, [tab, activeSubId]);

  const loadFolders = useCallback(async () => {
    // Load folders only when a specific subcategory is selected — folders live inside subs.
    if (!activeSubId || activeSubId === "all") {
      setFolders([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/library/folders`, {
        params: { subcategory_id: activeSubId },
        headers: { Authorization: `Bearer ${currentToken()}` },
      });
      setFolders(res.data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(VIEWER_TOKEN_KEY);
        setUnlocked(false);
      }
    }
  }, [activeSubId]);

  // Reset subcategory filter when switching tabs
  useEffect(() => {
    setActiveSubId("all");
  }, [tab]);

  useEffect(() => {
    if (unlocked) {
      loadSubcategories();
      loadAssets();
      loadFolders();
    }
  }, [unlocked, loadSubcategories, loadAssets, loadFolders]);

  const createSubcategory = async () => {
    const name = newSubName.trim();
    if (!name) return;
    setSubBusy(true);
    try {
      const res = await axios.post(
        `${API}/library/admin/subcategories`,
        { category: tab, name },
        { headers: { Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}` } },
      );
      setNewSubName("");
      setAddSubOpen(false);
      await loadSubcategories();
      if (res.data?.subcategory?.id) setActiveSubId(res.data.subcategory.id);
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not create subcategory.");
    } finally {
      setSubBusy(false);
    }
  };

  const deleteSubcategory = async (sub) => {
    if (!window.confirm(`Delete subcategory "${sub.name}"? Any assets under it must be moved first.`)) return;
    try {
      await axios.delete(`${API}/library/admin/subcategories/${sub.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}` },
      });
      if (activeSubId === sub.id) setActiveSubId("all");
      await loadSubcategories();
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed.");
    }
  };

  const renameSubcategory = async (sub) => {
    const next = window.prompt("Rename subcategory:", sub.name);
    if (!next || next.trim() === sub.name) return;
    try {
      await axios.patch(
        `${API}/library/admin/subcategories/${sub.id}`,
        { name: next.trim() },
        { headers: { Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}` } },
      );
      await loadSubcategories();
    } catch (e) {
      alert(e?.response?.data?.detail || "Rename failed.");
    }
  };

  const editShareName = async (kind, item) => {
    const path = kind === "folder" ? "folders" : "subcategories";
    const next = window.prompt(
      `Recipient-facing name — what partners see in the email subject and on the shared page.\nLeave blank to use "${item.name}".`,
      item.share_name || "",
    );
    if (next === null) return;
    try {
      await axios.patch(
        `${API}/library/admin/${path}/${item.id}`,
        { share_name: next.trim() },
        { headers: { Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}` } },
      );
      if (kind === "folder") await loadFolders();
      else await loadSubcategories();
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not save recipient-facing name.");
    }
  };

  const handleDownload = async (asset) => {
    try {
      const res = await fetch(`${API}/library/assets/${asset.id}/download`, {
        headers: { Authorization: `Bearer ${currentToken()}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = asset.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Download failed.");
    }
  };

  const handleDelete = async (asset) => {
    if (!window.confirm(`Delete "${asset.filename}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/library/admin/assets/${asset.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}` },
      });
      setAssets((prev) => prev.filter((a) => a.id !== asset.id));
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed (are you logged in as admin?)");
    }
  };

  const handleFeature = async (asset) => {
    try {
      await axios.patch(
        `${API}/library/admin/assets/${asset.id}/feature`,
        { featured: !asset.featured },
        { headers: { Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}` } },
      );
      setAssets((prev) => prev.map((a) => (a.id === asset.id ? { ...a, featured: !asset.featured } : a)));
    } catch (e) {
      alert(e?.response?.data?.detail || "Feature toggle failed (are you logged in as admin?)");
    }
  };

  const handleMove = async (asset, newSubId) => {
    if (!newSubId || newSubId === asset.subcategory_id) return;
    try {
      await axios.patch(
        `${API}/library/admin/assets/${asset.id}/subcategory`,
        { subcategory_id: newSubId },
        { headers: { Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}` } },
      );
      // If we're filtering by subcategory and moved it out, remove from the list.
      // Otherwise just update the record inline.
      setAssets((prev) => {
        if (activeSubId !== "all" && newSubId !== activeSubId) {
          return prev.filter((a) => a.id !== asset.id);
        }
        // Moving subcategory clears folder_id on the backend
        return prev.map((a) => (a.id === asset.id ? { ...a, subcategory_id: newSubId, folder_id: null } : a));
      });
    } catch (e) {
      alert(e?.response?.data?.detail || "Move failed.");
    }
  };

  const handleMoveFolder = async (asset, newFolderId) => {
    // "" / "ungrouped" — clear folder
    const target = newFolderId || null;
    if ((asset.folder_id || null) === target) return;
    try {
      await axios.patch(
        `${API}/library/admin/assets/${asset.id}/folder`,
        { folder_id: target },
        { headers: { Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}` } },
      );
      setAssets((prev) => prev.map((a) => (a.id === asset.id ? { ...a, folder_id: target } : a)));
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not move to folder.");
    }
  };

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name || !activeSubId || activeSubId === "all") return;
    setFolderBusy(true);
    try {
      const res = await axios.post(
        `${API}/library/admin/folders`,
        { subcategory_id: activeSubId, name },
        { headers: { Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}` } },
      );
      setNewFolderName("");
      setAddFolderOpen(false);
      await loadFolders();
      const newId = res.data?.folder?.id;
      if (newId) setExpandedFolders((prev) => ({ ...prev, [newId]: true }));
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not create folder.");
    } finally {
      setFolderBusy(false);
    }
  };

  const renameFolder = async (folder) => {
    const next = window.prompt("Rename folder:", folder.name);
    if (!next || next.trim() === folder.name) return;
    try {
      await axios.patch(
        `${API}/library/admin/folders/${folder.id}`,
        { name: next.trim() },
        { headers: { Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}` } },
      );
      await loadFolders();
    } catch (e) {
      alert(e?.response?.data?.detail || "Rename failed.");
    }
  };

  const describeFolder = async (folder) => {
    const next = window.prompt(
      'One-line "what it is / what you do with it" — shown on shared packets:',
      folder.description || "",
    );
    if (next === null) return;
    try {
      await axios.patch(
        `${API}/library/admin/folders/${folder.id}`,
        { description: next.trim() },
        { headers: { Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}` } },
      );
      await loadFolders();
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not save description.");
    }
  };

  const deleteFolder = async (folder) => {
    if (!window.confirm(`Delete folder "${folder.name}"? Assets inside will move to Ungrouped.`)) return;
    try {
      await axios.delete(`${API}/library/admin/folders/${folder.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}` },
      });
      await loadFolders();
      // Mark those assets as ungrouped in local state
      setAssets((prev) => prev.map((a) => (a.folder_id === folder.id ? { ...a, folder_id: null } : a)));
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed.");
    }
  };

  const signOut = () => {
    localStorage.removeItem(VIEWER_TOKEN_KEY);
    setUnlocked(false);
    setAssets([]);
  };

  if (!ready) return null;
  if (!unlocked) return <PasswordGate onUnlock={() => setUnlocked(true)} />;

  const activeCat = CATEGORIES.find((c) => c.key === tab);

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="library-page">
      {/* Header */}
      <header
        className="text-white"
        style={{
          background:
            "linear-gradient(135deg, #08192F 0%, #0F2A47 50%, #17385e 100%)",
        }}
      >
        <div className="max-w-6xl mx-auto px-6 py-8 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <a href="/" className="inline-flex bg-white rounded-2xl px-3 py-1.5 shadow">
              <img src={ASSETS.logoHorizontal} alt="Great Rate Check" className="h-9 sm:h-11 w-auto" />
            </a>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-[#A6D24A]">Partner Library</div>
              <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
                Great Rate Check — Partner Asset Library
              </h1>
              <p className="text-sm text-white/70 mt-1 max-w-2xl">
                Marketing, messaging, and process documents for our lending partners.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <>
                <span className="hidden sm:inline text-[11px] uppercase tracking-widest bg-[#A6D24A]/20 text-[#A6D24A] px-2 py-1 rounded-md">
                  Admin
                </span>
                <a
                  href="/admin"
                  data-testid="library-back-to-admin"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-white/20 hover:bg-white/10 px-3 py-1.5 text-sm"
                >
                  <LayoutDashboard className="w-3.5 h-3.5" /> Admin
                </a>
              </>
            )}
            <button
              onClick={signOut}
              data-testid="library-signout"
              className="inline-flex items-center gap-1.5 rounded-lg border border-white/20 hover:bg-white/10 px-3 py-1.5 text-sm"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
        {/* Category tabs */}
        <nav className="max-w-6xl mx-auto px-6 pb-4 flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              onClick={() => setTab(c.key)}
              data-testid={`library-tab-${c.key}`}
              className={`px-4 py-2 rounded-full text-sm transition ${
                tab === c.key
                  ? "bg-white text-[#0F2A47] shadow"
                  : "bg-white/10 text-white hover:bg-white/20"
              }`}
            >
              {c.label}
            </button>
          ))}
        </nav>
      </header>

      {/* Body */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-xl font-semibold text-[#0F2A47]">{activeCat.label}</h2>
            <p className="text-sm text-[#59564C] mt-1">{activeCat.blurb}</p>
          </div>
          {isAdmin && tab !== "print" && tab !== "replays" && (
            <button
              onClick={() => setShowUpload(true)}
              data-testid="library-upload-open"
              className="inline-flex items-center gap-2 rounded-xl bg-[#1F8F8B] hover:bg-[#177977] text-white px-4 py-2.5 text-sm shadow"
            >
              <Upload className="w-4 h-4" /> Upload to {activeCat.label}
            </button>
          )}
        </div>

        {/* Subcategory chip bar */}
        {tab !== "print" && tab !== "replays" && (
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="library-subcategory-bar">
          <button
            onClick={() => setActiveSubId("all")}
            data-testid="library-sub-chip-all"
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
              activeSubId === "all"
                ? "bg-[#0F2A47] text-white shadow"
                : "bg-white border border-[#E6E2D6] text-[#59564C] hover:border-[#1F8F8B]"
            }`}
          >
            All
          </button>
          {subcategories.map((s) => (
            <span key={s.id} className="inline-flex items-center gap-0.5">
              <button
                onClick={() => setActiveSubId(s.id)}
                data-testid={`library-sub-chip-${s.id}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                  activeSubId === s.id
                    ? "bg-[#0F2A47] text-white shadow"
                    : "bg-white border border-[#E6E2D6] text-[#59564C] hover:border-[#1F8F8B]"
                }`}
              >
                {s.name}
              </button>
              {isAdmin && s.name !== "General" && (
                <span className="inline-flex items-center gap-0.5 ml-0.5">
                  <button
                    onClick={() => renameSubcategory(s)}
                    title={`Rename "${s.name}"`}
                    data-testid={`library-sub-rename-${s.id}`}
                    className="text-[#7A7668] hover:text-[#0F2A47] p-1 rounded-md hover:bg-[#F4F2EB]"
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => editShareName("subcategory", s)}
                    title={s.share_name ? `Partners see: ${s.share_name}` : "Set recipient-facing name (what partners see)"}
                    data-testid={`library-sub-sharename-${s.id}`}
                    className={`p-1 rounded-md hover:bg-[#F4F2EB] ${s.share_name ? "text-[#1F8F8B]" : "text-[#7A7668] hover:text-[#0F2A47]"}`}
                  >
                    <Eye className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => deleteSubcategory(s)}
                    title={`Delete "${s.name}"`}
                    data-testid={`library-sub-delete-${s.id}`}
                    className="text-[#7A7668] hover:text-red-600 p-1 rounded-md hover:bg-[#F4F2EB]"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
            </span>
          ))}
          {isAdmin && (
            addSubOpen ? (
              <span className="inline-flex items-center gap-1 bg-white border border-[#1F8F8B] rounded-full pl-3 pr-1 py-0.5">
                <input
                  autoFocus
                  value={newSubName}
                  onChange={(e) => setNewSubName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") createSubcategory();
                    if (e.key === "Escape") { setAddSubOpen(false); setNewSubName(""); }
                  }}
                  placeholder="New subcategory"
                  data-testid="library-sub-new-input"
                  className="text-xs w-40 outline-none py-1"
                />
                <button
                  onClick={createSubcategory}
                  disabled={subBusy || !newSubName.trim()}
                  data-testid="library-sub-new-save"
                  className="inline-flex items-center bg-[#1F8F8B] hover:bg-[#177977] text-white rounded-full p-1 disabled:opacity-60"
                >
                  {subBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                </button>
                <button
                  onClick={() => { setAddSubOpen(false); setNewSubName(""); }}
                  className="text-[#7A7668] hover:text-[#0F2A47] p-1"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ) : (
              <button
                onClick={() => setAddSubOpen(true)}
                data-testid="library-sub-add-open"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-white border border-dashed border-[#1F8F8B] text-[#1F8F8B] hover:bg-[#1F8F8B]/5"
              >
                <Plus className="w-3 h-3" /> Add subcategory
              </button>
            )
          )}
        </div>
        )}

        {tab === "replays" ? (
          <ReplaysGrid
            getAuthHeader={() => {
              const t = localStorage.getItem(ADMIN_TOKEN_KEY) || localStorage.getItem(VIEWER_TOKEN_KEY);
              return t ? { Authorization: `Bearer ${t}` } : {};
            }}
          />
        ) : tab === "print" ? (
          <PrintCardsPanel />
        ) : loading ? (
          <div className="py-24 flex items-center justify-center text-[#7A7668]">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading assets…
          </div>
        ) : assets.length === 0 && (activeSubId === "all" || folders.length === 0) ? (
          <div
            className="rounded-3xl border border-dashed border-[#D9D3BF] bg-white/60 py-16 text-center"
            data-testid="library-empty"
          >
            <FileText className="w-8 h-8 text-[#7A7668] mx-auto mb-2" />
            <p className="text-[#0F2A47] font-medium">Nothing here yet.</p>
            <p className="text-sm text-[#7A7668] mt-1">
              {isAdmin
                ? "Click Upload to add the first files to this category."
                : "Files will appear here once your team uploads them."}
            </p>
          </div>
        ) : activeSubId === "all" ? (
          // "All" view: keep the flat grid (no folder grouping) so cross-subcategory browsing stays quick
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <AnimatePresence>
              {assets.map((a) => (
                <AssetCard
                  key={a.id}
                  asset={a}
                  subcategories={subcategories}
                  folders={folders}
                  onDownload={handleDownload}
                  onDelete={handleDelete}
                  onShare={setShareAsset}
                  onPreview={setPreviewAsset}
                  onMove={handleMove}
                  onMoveFolder={handleMoveFolder}
                  onFeature={handleFeature}
                  isAdmin={isAdmin}
                />
              ))}
            </AnimatePresence>
          </div>
        ) : (
          // Specific subcategory: group assets into folders + an "Ungrouped" section
          <div className="space-y-6" data-testid="library-folder-groups">
            {/* Folder toolbar */}
            {isAdmin && (
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => {
                    const sub = subcategories.find((s) => s.id === activeSubId);
                    if (sub) setIssueTarget({ kind: "subcategory", id: sub.id, name: sub.share_name || sub.name });
                  }}
                  data-testid="library-issue-subcategory"
                  className="inline-flex items-center gap-1.5 rounded-full text-xs font-medium px-3 py-1.5 bg-[#0F2A47] text-white hover:bg-[#173a5f]"
                >
                  <Share2 className="w-3.5 h-3.5" /> Issue this subcategory
                </button>
                {addFolderOpen ? (
                  <span className="inline-flex items-center gap-1 bg-white border border-[#1F8F8B] rounded-full pl-3 pr-1 py-0.5">
                    <input
                      autoFocus
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") createFolder();
                        if (e.key === "Escape") { setAddFolderOpen(false); setNewFolderName(""); }
                      }}
                      placeholder="New folder name"
                      data-testid="library-folder-new-input"
                      className="text-xs w-44 outline-none py-1"
                    />
                    <button
                      onClick={createFolder}
                      disabled={folderBusy || !newFolderName.trim()}
                      data-testid="library-folder-new-save"
                      className="inline-flex items-center bg-[#1F8F8B] hover:bg-[#177977] text-white rounded-full p-1 disabled:opacity-60"
                    >
                      {folderBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    </button>
                    <button
                      onClick={() => { setAddFolderOpen(false); setNewFolderName(""); }}
                      className="text-[#7A7668] hover:text-[#0F2A47] p-1"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ) : (
                  <button
                    onClick={() => setAddFolderOpen(true)}
                    data-testid="library-folder-add-open"
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-white border border-dashed border-[#1F8F8B] text-[#1F8F8B] hover:bg-[#1F8F8B]/5"
                  >
                    <Plus className="w-3 h-3" /> New folder
                  </button>
                )}
              </div>
            )}

            {/* Folder groups */}
            {folders.map((folder) => {
              const folderAssets = assets.filter((a) => a.folder_id === folder.id);
              const isOpen = expandedFolders[folder.id] !== false; // default open
              return (
                <section key={folder.id} data-testid={`library-folder-${folder.id}`} className="rounded-2xl border border-[#EDE9DC] bg-white/60 overflow-hidden">
                  <header className="flex items-center gap-3 px-4 sm:px-5 py-3 bg-white border-b border-[#EDE9DC]">
                    <button
                      onClick={() => setExpandedFolders((p) => ({ ...p, [folder.id]: !isOpen }))}
                      data-testid={`library-folder-toggle-${folder.id}`}
                      className="inline-flex items-center gap-2 text-[#0F2A47] font-semibold text-sm hover:text-[#1F8F8B]"
                    >
                      <span className={`inline-block transition-transform ${isOpen ? "rotate-90" : ""}`}>▸</span>
                      {folder.name}
                      <span className="text-[11px] font-normal text-[#7A7668]">({folderAssets.length})</span>
                    </button>
                    <div className="ml-auto flex items-center gap-1">
                      {isAdmin && (
                        <>
                          <button
                            onClick={() => setIssueTarget({ kind: "folder", id: folder.id, name: folder.share_name || folder.name })}
                            data-testid={`library-folder-issue-${folder.id}`}
                            title={`Issue folder "${folder.name}"`}
                            className="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full bg-[#A6D24A] text-[#0F2A47] hover:bg-[#94bd41]"
                          >
                            <Share2 className="w-3 h-3" /> Issue
                          </button>
                          <button
                            onClick={() => renameFolder(folder)}
                            title="Rename folder"
                            data-testid={`library-folder-rename-${folder.id}`}
                            className="text-[#7A7668] hover:text-[#0F2A47] p-1 rounded-md hover:bg-[#F4F2EB]"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => describeFolder(folder)}
                            title={folder.description ? `Edit description: ${folder.description}` : "Add a one-line description (shown on shared packets)"}
                            data-testid={`library-folder-describe-${folder.id}`}
                            className={`p-1 rounded-md hover:bg-[#F4F2EB] ${folder.description ? "text-[#1F8F8B]" : "text-[#7A7668] hover:text-[#0F2A47]"}`}
                          >
                            <FileText className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => editShareName("folder", folder)}
                            title={folder.share_name ? `Partners see: ${folder.share_name}` : "Set recipient-facing name (what partners see)"}
                            data-testid={`library-folder-sharename-${folder.id}`}
                            className={`p-1 rounded-md hover:bg-[#F4F2EB] ${folder.share_name ? "text-[#1F8F8B]" : "text-[#7A7668] hover:text-[#0F2A47]"}`}
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => deleteFolder(folder)}
                            title="Delete folder"
                            data-testid={`library-folder-delete-${folder.id}`}
                            className="text-[#7A7668] hover:text-red-600 p-1 rounded-md hover:bg-[#F4F2EB]"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </header>
                  {isOpen && (
                    <div className="p-4 sm:p-5">
                      {folder.description && (
                        <p className="text-xs text-[#59564C] italic mb-3 -mt-1" data-testid={`library-folder-desc-${folder.id}`}>{folder.description}</p>
                      )}
                      {folderAssets.length === 0 ? (
                        <p className="text-xs text-[#7A7668] italic">
                          Empty folder. {isAdmin ? "Upload files with this folder selected, or move existing files here." : "Ask an admin to add files."}
                        </p>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                          <AnimatePresence>
                            {folderAssets.map((a) => (
                              <AssetCard
                                key={a.id}
                                asset={a}
                                subcategories={subcategories}
                                folders={folders}
                                onDownload={handleDownload}
                                onDelete={handleDelete}
                                onShare={setShareAsset}
                                onPreview={setPreviewAsset}
                                onMove={handleMove}
                                onMoveFolder={handleMoveFolder}
                                onFeature={handleFeature}
                                isAdmin={isAdmin}
                              />
                            ))}
                          </AnimatePresence>
                        </div>
                      )}
                    </div>
                  )}
                </section>
              );
            })}

            {/* Ungrouped assets */}
            {(() => {
              const ungrouped = assets.filter((a) => !a.folder_id);
              const isOpen = expandedFolders.ungrouped !== false;
              if (ungrouped.length === 0 && folders.length > 0) return null;
              return (
                <section data-testid="library-folder-ungrouped" className="rounded-2xl border border-[#EDE9DC] bg-white/60 overflow-hidden">
                  <header className="flex items-center gap-3 px-4 sm:px-5 py-3 bg-white border-b border-[#EDE9DC]">
                    <button
                      onClick={() => setExpandedFolders((p) => ({ ...p, ungrouped: !isOpen }))}
                      className="inline-flex items-center gap-2 text-[#0F2A47] font-semibold text-sm hover:text-[#1F8F8B]"
                      data-testid="library-folder-toggle-ungrouped"
                    >
                      <span className={`inline-block transition-transform ${isOpen ? "rotate-90" : ""}`}>▸</span>
                      Ungrouped
                      <span className="text-[11px] font-normal text-[#7A7668]">({ungrouped.length})</span>
                    </button>
                  </header>
                  {isOpen && (
                    <div className="p-4 sm:p-5">
                      {ungrouped.length === 0 ? (
                        <p className="text-xs text-[#7A7668] italic">Everything here has been organized into folders.</p>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                          <AnimatePresence>
                            {ungrouped.map((a) => (
                              <AssetCard
                                key={a.id}
                                asset={a}
                                subcategories={subcategories}
                                folders={folders}
                                onDownload={handleDownload}
                                onDelete={handleDelete}
                                onShare={setShareAsset}
                                onPreview={setPreviewAsset}
                                onMove={handleMove}
                                onMoveFolder={handleMoveFolder}
                                onFeature={handleFeature}
                                isAdmin={isAdmin}
                              />
                            ))}
                          </AnimatePresence>
                        </div>
                      )}
                    </div>
                  )}
                </section>
              );
            })()}
          </div>
        )}
      </main>

      <footer className="max-w-6xl mx-auto px-6 pb-10 text-xs text-[#7A7668]">
        Great Rate Check · NEXA Lending · Materials on this page are for authorized partners only.
      </footer>

      <AnimatePresence>
        {showUpload && (
          <UploadPanel
            category={tab}
            subcategories={subcategories}
            folders={folders}
            defaultSubcategoryId={activeSubId !== "all" ? activeSubId : (subcategories[0]?.id ?? "")}
            defaultFolderId=""
            onClose={() => setShowUpload(false)}
            onUploaded={() => { loadAssets(); loadFolders(); }}
          />
        )}
        {shareAsset && (
          <ShareModal asset={shareAsset} onClose={() => setShareAsset(null)} />
        )}
        {issueTarget && (
          <IssueBundleModal
            target={issueTarget}
            onClose={() => setIssueTarget(null)}
          />
        )}
        {previewAsset && (
          <PreviewModal
            asset={previewAsset}
            onClose={() => setPreviewAsset(null)}
            onDownload={handleDownload}
            onShare={setShareAsset}
            isAdmin={isAdmin}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
